// packages/core/src/learning/school-modules/tax-optimization.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'tax-optimization' school. LearningModule, surface 'school'.
//   module id   = 'school-tax-optimization'  (matches exhibit nativeModuleId)
//   homeSchool  = 'tax-optimization'
// PROMOTED: three authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_TAX_OPTIMIZATION_MODULE: LearningModule = {
	id: 'school-tax-optimization',
	title: 'Tax Optimization',
	description:
		'The lens that looks past gross income to after-tax reality — timing, records, accounts, and keeping the tax tail from wagging the dog.',
	emoji: '🧾',
	accentColor: '#4f8f3a',
	tier: 'control',
	pillar: 'cash-flow-control',
	order: 0,
	surface: 'school',
	homeSchool: 'tax-optimization',
	lessons: [
		{
			id: 'school-tax-optimization-01',
			title: 'After tax money is the real money',
			description:
				'Learn why tax optimization starts by looking past gross income and seeing what actually funds your life.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['tax-optimization'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['tax-optimization', 'after-tax-income', 'take-home-pay'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'after tax reality',
				'gross income illusion',
				'real spending power',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'A job offer, raise, side gig, bonus, or investment gain can look bigger before taxes enter the room. Tax optimization starts with a simple reality check: the number you see first is not always the number that changes your life.',
				},
				{
					type: 'paragraph',
					text: 'This school believes after tax money is the money that actually funds rent, bills, savings, investing, family support, and freedom. It does not ignore gross income. It just refuses to treat gross income like the final answer.',
				},
				{
					type: 'bullet_list',
					items: [
						'What did you earn?',
						'What did you keep?',
						'When do taxes apply?',
						'What does the money actually support?',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is planning your life around the headline number instead of the money that reaches your hands.',
				},
				{
					type: 'example',
					text: 'Someone gets a $5,000 bonus and mentally spends the full amount before taxes, withholding, debt, and bills are considered. Tax optimization does not say the bonus is bad. It says the real decision starts after you understand what the bonus actually becomes.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that after tax thinking can feel less exciting. It can make a raise, bonus, or side income feel smaller than the story you wanted. But that honesty is useful because it keeps your plan grounded in the money you can actually use.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to your Dashboard. Look at income, expenses, and savings through the lens of what really lands in your life, not what sounded impressive before taxes and obligations.',
				},
				{
					type: 'key_takeaway',
					text: 'Tax optimization begins when you stop confusing income with usable money.',
				},
			],
			audioScript:
				'Tax optimization starts with a truth that sounds obvious, but people still forget it all the time: the number you earn is not always the number you can use. Gross income gets the attention. It is the number in job posts, offer letters, bonus announcements, side gig screenshots, and big income goals. But your life is not funded by the headline number. Your life is funded by what is left after taxes, withholding, benefits, bills, debt payments, and the other obligations that sit between income and freedom.\n\nThis lens is not trying to make income feel worse. More income can absolutely matter. A raise can change your options. A bonus can give breathing room. A side gig can help build momentum. But tax optimization asks you to slow down before you mentally spend the full number. It asks, “What does this actually become after taxes?” That one question can prevent a lot of confusion.\n\nImagine someone gets a $5,000 bonus. In their head, that might feel like $5,000 available for a trip, debt, investing, furniture, car repairs, or a move. Then the actual deposit hits, and it is smaller than expected. Now the plan feels disappointing, even though the bonus was still helpful. The issue was not the tax system alone. The issue was planning around the wrong number.\n\nThe same thing happens with side income. Someone makes an extra $800 in a month and feels like they are ahead. But if no taxes were withheld, that money may come with a future obligation. Tax optimization does not say, “Do not earn extra money.” It says, “Understand what kind of income this is, what records you need, and what part may not truly be yours to spend.”\n\nThe tradeoff is emotional. After tax thinking can make wins feel smaller. It can interrupt excitement. It can make a raise feel less dramatic or a bonus feel less magical. But it also gives you something better than hype: accuracy. Accurate numbers let you make cleaner decisions. You can decide what to save, what to spend, what to hold back, and what to test without pretending the full headline number is available.\n\nInside Nisk, this connects to the Dashboard. Your Dashboard is not there to flatter you. It is there to show the money picture you are actually living with. Tax optimization uses that picture to ask better questions. What do you really take home? What recurring costs are already spoken for? What income changes would actually change your runway, savings rate, or freedom timeline?\n\nThe point is not to become obsessed with taxes. The point is to stop being surprised by them. The money that matters most is not the number that sounds good. It is the number that gives you real options.',
			flashcards: [
				{
					id: 'school-tax-optimization-01-fc-1',
					role: 'main_idea',
					front: 'What money funds your life?',
					back: 'The money left after taxes and obligations.',
				},
				{
					id: 'school-tax-optimization-01-fc-2',
					role: 'watch_out',
					front: 'What is the gross income trap?',
					back: 'Planning around the headline number instead of usable money.',
				},
				{
					id: 'school-tax-optimization-01-fc-3',
					role: 'use_it',
					front: 'How do you apply this lens?',
					back: 'Compare income changes against what actually reaches your life.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Review your real money picture',
			},
			revision: 1,
		},
		{
			id: 'school-tax-optimization-02',
			title: 'Timing changes the tax story',
			description:
				'Learn why tax optimization pays attention to when income, deductions, accounts, and gains show up.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['tax-optimization'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['tax-optimization', 'tax-timing', 'withholding'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'tax timing',
				'account tradeoffs',
				'planning before filing',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Tax season feels like the moment taxes happen, but many tax outcomes are shaped earlier. Income arrives. Accounts get funded. Stock is sold. A side gig starts. A move happens. Records are kept or ignored.',
				},
				{
					type: 'paragraph',
					text: 'Tax optimization believes timing matters because the same money can be treated differently depending on when it appears, where it sits, and what decision created it. This is not about loopholes. It is about seeing taxes before they become a surprise.',
				},
				{
					type: 'bullet_list',
					items: [
						'When is the money earned?',
						'When is it taxed?',
						'What records prove it?',
						'What choice created the tax result?',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is waiting until filing season to think about decisions that already happened.',
				},
				{
					type: 'example',
					text: 'Someone starts freelance work and spends the deposits like regular paychecks. Months later, they realize no employer was withholding taxes for that income. Tax optimization notices the issue earlier: different income can create different responsibilities.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is complexity. Timing can matter, but tax rules are personal and can change. Chasing the perfect tax move can distract from the bigger decision, especially if you do not understand the full cost, restrictions, or paperwork.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens works best as a planning prompt. Before a big money move, write the tax questions it creates. You are not trying to answer everything alone. You are learning what deserves attention before the decision is locked in.',
				},
				{
					type: 'key_takeaway',
					text: 'Taxes are easier to understand when you notice the decision before the filing deadline.',
				},
			],
			audioScript:
				'A lot of people think taxes happen at tax season. That makes sense because that is when the forms show up, the software opens, the documents get hunted down, and the refund or bill becomes real. But tax optimization sees tax season differently. It is not when every tax decision happens. It is when many earlier decisions get counted.\n\nThat is why timing matters. Money can be earned, withheld, deferred, sold, deducted, reported, or ignored at different points. Those timing differences can affect how the money feels and how prepared you are for the tax result. This lens is not about pretending taxes are simple. It is about noticing that the tax story often starts before the filing deadline.\n\nThink about side income. If you have a normal paycheck, taxes may be withheld before the money reaches your bank account. You still need to understand your pay, but at least part of the tax process is happening automatically. With freelance work, contract work, selling goods, or other income, the deposit can feel like regular take home pay. But it may not have the same withholding built in. If you spend it all, filing season can become a surprise. Tax optimization does not say, “Never make side income.” It says, “Different income can create different responsibilities, so do not treat every deposit the same.”\n\nTiming also shows up with accounts. Some accounts change when taxes are paid. Some may give a benefit now but create rules later. Some may be useful for long term planning, but they can have limits, restrictions, or tradeoffs. Tax optimization wants you to understand the basic timing question: am I dealing with taxes now, later, or in a different way? That question alone can make the decision clearer.\n\nBut the tradeoff is complexity. Tax optimization can go wrong when people chase tax moves they barely understand. A tax benefit is not automatically a good decision. A deduction is not free money. A tax advantaged account is not magic. A strategy that fits one person may be wrong for another because income, family situation, state rules, account access, deadlines, cash flow, and goals can differ. This is why Nisk keeps the content educational. The goal is not to tell you exactly what to do. The goal is to help you ask smarter questions before the decision becomes permanent.\n\nInside Nisk, this lesson works as a planning habit. Before a major money move, pause and ask: what tax questions does this create? Is this income withheld or not? Do I need records? Does timing matter? Does this account have rules? Would a professional be worth asking because the details are personal?\n\nYou do not need to become a tax expert to become less surprised. You need to notice that taxes are part of the decision, not just the paperwork afterward.',
			flashcards: [
				{
					id: 'school-tax-optimization-02-fc-1',
					role: 'main_idea',
					front: 'Why does timing matter?',
					back: 'Tax results are often shaped before filing season.',
				},
				{
					id: 'school-tax-optimization-02-fc-2',
					role: 'watch_out',
					front: 'What is the filing season trap?',
					back: 'Waiting to think about taxes after decisions already happened.',
				},
				{
					id: 'school-tax-optimization-02-fc-3',
					role: 'use_it',
					front: 'What should you ask first?',
					back: 'Ask what tax questions a money move creates.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'List your tax questions',
				prompt: 'Write the income, timing, account, and record questions this money move creates before you commit to it.',
			},
			revision: 1,
		},
		{
			id: 'school-tax-optimization-03',
			title: 'Do not let taxes drive everything',
			description:
				'Learn where tax optimization breaks down when the tax benefit becomes bigger than the life decision.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['tax-optimization'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['tax-optimization', 'tax-tail', 'full-decision-view'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'tax tail risk',
				'complexity cost',
				'full decision view',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Tax optimization is useful because taxes matter. But it can become dangerous when the tax angle takes over the whole decision. A move can be tax efficient and still be wrong for your cash flow, stress, time, risk, or goals.',
				},
				{
					type: 'paragraph',
					text: 'This school believes tax awareness should support better decisions, not replace judgment. The question is not only, “Can this reduce taxes?” The better question is, “Does this still make sense after I count the full tradeoff?”',
				},
				{
					type: 'bullet_list',
					items: [
						'Tax impact',
						'Cash flow impact',
						'Complexity cost',
						'Flexibility cost',
						'Risk introduced',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating a tax benefit like proof that the whole decision is smart.',
				},
				{
					type: 'example',
					text: 'Someone buys something mostly because they heard it can create a deduction. If the purchase is not useful, affordable, or well understood, the tax angle may be a distraction. Tax optimization notices the benefit, but it also asks what the decision costs before and after the tax effect.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that tax thinking can reward complexity. More accounts, more paperwork, more timing rules, and more strategies can look sophisticated. But sophistication is not the same as clarity.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, test the full decision. If a tax idea affects income, spending, saving, or future options, run the broader scenario instead of staring only at the tax benefit.',
				},
				{
					type: 'key_takeaway',
					text: 'A tax benefit is one input. It is not the whole decision.',
				},
			],
			audioScript:
				'Tax optimization has a very real strength: it teaches you not to ignore a line item that can quietly change your money life. Taxes affect paychecks, side income, investing, retirement accounts, housing decisions, business decisions, and timing. Ignoring taxes can make your plan look cleaner than it really is.\n\nBut this lens has a trap. Once you start noticing tax benefits, it can become tempting to treat the tax angle as the whole decision. That is where tax optimization breaks down. A move can reduce taxes and still be a poor fit. A purchase can create a deduction and still cost more than it helps. An account can have tax advantages and still have restrictions you do not like. A strategy can sound sophisticated and still create stress, paperwork, or risk that is not worth it.\n\nThe classic warning is simple: do not let the tax tail wag the dog. In plain English, do not make the entire life decision just because one tax detail looks attractive. The tax result matters, but it is one input. You still have to count cash flow, flexibility, complexity, risk, timing, and what the decision is actually supposed to do for your life.\n\nImagine someone buys equipment, signs up for a service, starts a business expense, or makes an investment mainly because someone says it is deductible. That word can make a decision feel smarter than it is. But a deduction does not mean free. It usually means the cost may reduce taxable income in some way, depending on rules and personal details. You still spent money. You still took on the obligation. You still have to keep records. You still need the purchase to make sense beyond the tax treatment.\n\nTax optimization at its best is humble. It asks good questions without pretending every answer is simple. What is the tax impact? What is the actual cash cost? Does this create a future restriction? Does it add paperwork? Does it reduce flexibility? Does it create risk? Would I still consider this if the tax benefit were smaller than expected? Those questions protect you from making tax flavored decisions that do not fit your real life.\n\nInside Nisk, this is where simulation matters. If a tax idea changes your income, spending, saving, or future options, test the broader decision. Do not stare only at the tax benefit. Ask what happens to your runway, monthly cash flow, freedom plan, and ability to handle surprises. Taxes are part of the math, but they are not the entire life.\n\nThe goal is not to ignore tax planning. The goal is to keep tax planning in its proper place. Tax optimization should make your decisions clearer, not more complicated just to feel clever.',
			flashcards: [
				{
					id: 'school-tax-optimization-03-fc-1',
					role: 'main_idea',
					front: 'What is the tax tail trap?',
					back: 'Letting a tax benefit drive the whole decision.',
				},
				{
					id: 'school-tax-optimization-03-fc-2',
					role: 'watch_out',
					front: 'Is a deduction free money?',
					back: 'No. It can reduce tax impact, but the cost and rules still matter.',
				},
				{
					id: 'school-tax-optimization-03-fc-3',
					role: 'use_it',
					front: 'How do you test tax ideas?',
					back: 'Compare the full tradeoff, not just the tax benefit.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate the full tradeoff',
			},
			revision: 1,
		},
	],
};
